import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Modal, Select } from '@/shared/ui/primitives';
import type { SupportedLocale } from '../api/translations.api';

interface Props {
  open: boolean;
  namespaces: string[];
  locales: SupportedLocale[];
  loading?: boolean;
  onSubmit: (input: { key: string; namespace: string; translations: Record<string, string> }) => Promise<unknown> | void;
  onCancel: () => void;
}

/**
 * Required locales (those that must have a value at create time) are
 * the two we have full coverage for: ar and en. Other locales are
 * optional inputs and can be filled in later through inline editing.
 */
const REQUIRED_LOCALES = new Set(['ar', 'en']);

export default function TranslationCreateDialog({
  open,
  namespaces,
  locales,
  loading,
  onSubmit,
  onCancel,
}: Props) {
  const { t } = useTranslation();
  const [key, setKey] = useState('');
  const [namespace, setNamespace] = useState<string>('common');
  const [values, setValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setValues((prev) => {
      const next = { ...prev };
      for (const l of locales) if (next[l.code] == null) next[l.code] = '';
      return next;
    });
  }, [open, locales]);

  const reset = () => {
    setKey('');
    setNamespace('common');
    setValues(Object.fromEntries(locales.map((l) => [l.code, ''])));
    setError(null);
  };

  const handleOk = async () => {
    if (!key.trim()) return setError(t('translations.validation.keyRequired'));
    if (!namespace.trim()) return setError(t('translations.validation.moduleRequired'));
    for (const l of locales) {
      if (REQUIRED_LOCALES.has(l.code) && !values[l.code]?.trim()) {
        return setError(t('translations.validation.valueRequired', { language: localeLabel(l, t) }));
      }
    }
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(values)) {
      if (v.trim()) out[k] = v.trim();
    }
    setError(null);
    await onSubmit({ key: key.trim(), namespace: namespace.trim(), translations: out });
    reset();
  };

  return (
    <Modal
      open={open}
      title={t('translations.create.title')}
      onCancel={() => {
        reset();
        onCancel();
      }}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button onClick={() => { reset(); onCancel(); }}>{t('common.cancel')}</Button>
          <Button type="primary" onClick={handleOk} loading={loading}>{t('common.create')}</Button>
        </div>
      }
    >
      <div style={{ display: 'grid', gap: 12, maxHeight: '60vh', overflowY: 'auto' }}>
        <label>
          <div style={{ marginBottom: 4, fontWeight: 500 }}>{t('translations.create.technicalKey')}</div>
          <Input
            value={key}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setKey(e.target.value)}
            placeholder={t('translations.create.keyPlaceholder')}
          />
        </label>
        <label>
          <div style={{ marginBottom: 4, fontWeight: 500 }}>{t('translations.columns.module')}</div>
          <Select
            value={namespace}
            onChange={(v) => setNamespace(String(v))}
            options={namespaces.map((n) => ({ value: n, label: moduleLabel(n, t) }))}
            allowClear={false}
            showSearch
            style={{ width: '100%' }}
          />
        </label>
        {locales.map((l) => (
          <label key={l.code}>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>
              {localeLabel(l, t)}{' '}
              {REQUIRED_LOCALES.has(l.code) ? <span style={{ color: '#d4380d' }}>*</span> : (
                <span style={{ color: '#999', fontWeight: 400, fontSize: 12 }}>{t('translations.create.optional')}</span>
              )}
            </div>
            <Input
              value={values[l.code] ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setValues((p) => ({ ...p, [l.code]: e.target.value }))
              }
              dir={l.rtl ? 'rtl' : 'ltr'}
            />
          </label>
        ))}
        {error && <div style={{ color: '#d4380d' }}>{error}</div>}
      </div>
    </Modal>
  );
}

function localeLabel(locale: SupportedLocale, t: (key: string, options?: any) => string): string {
  if (locale.code === 'ar') return t('translations.languages.ar');
  if (locale.code === 'en') return t('translations.languages.en');
  return locale.nativeName || locale.name || locale.code.toUpperCase();
}

function moduleLabel(namespace: string, t: (key: string, options?: any) => string): string {
  const translated = t(`translations.modules.${String(namespace).toLowerCase()}`, { defaultValue: '' });
  if (translated) return translated;
  return namespace
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
